import { ChevronDown, LogOut, Mail, Menu, MessageSquare, Puzzle, ShieldCheck } from "lucide-react"
import { useState } from "react"
import { Link, NavLink } from "react-router-dom"
import { useLogout } from "~/auth/use-logout"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { cn } from "~/common/cn"
import {
  aboutPath,
  contactPath,
  dashboardPath,
  faqPath,
  internalAdminContactMessagesPath,
  internalAdminDashboardPath,
  internalAdminLogsPath,
  internalAdminPuzzlesPath,
  loginPath,
  profileEditPath,
  registerPuzzlePath,
  rootPath,
  userPublicProfilePath,
} from "~/common/paths"
import mascotUrl from "~/images/wp-mascot-logo-crop.png"
import { Avatar, AvatarFallback, AvatarImage } from "~/ui/avatar"
import { Button } from "~/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/ui/dropdown-menu"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "~/ui/sheet"

const initialOf = (firstName: string) => firstName.trim().charAt(0).toUpperCase() || "?"

const adminLinks = [
  { to: internalAdminDashboardPath({}), label: "Users", icon: ShieldCheck },
  { to: internalAdminPuzzlesPath({}), label: "Puzzles", icon: Puzzle },
  { to: internalAdminLogsPath({}), label: "Logs", icon: MessageSquare },
  { to: internalAdminContactMessagesPath({}), label: "Contact", icon: Mail },
]

const AdminMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1 text-white hover:bg-white/10 hover:text-white"
      >
        Admin
        <ChevronDown size={14} aria-hidden />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      {adminLinks.map((link) => (
        <DropdownMenuItem key={link.to} asChild>
          <Link to={link.to} className="flex items-center gap-2">
            <link.icon size={16} aria-hidden />
            {link.label}
          </Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
)

type AuthedViewer = {
  id: string
  firstName: string
  fullName: string
  email?: string | null
  avatarThumbUrl?: string | null
}

const UserMenu = ({ viewer }: { viewer: AuthedViewer }) => {
  const logout = useLogout()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-white/10"
          aria-label="Account menu"
        >
          <Avatar className="h-8 w-8 ring-2 ring-white/60">
            {viewer.avatarThumbUrl ? (
              <AvatarImage src={viewer.avatarThumbUrl} alt="" className="object-cover" />
            ) : null}
            <AvatarFallback className="bg-white text-sm font-semibold text-mountain">
              {initialOf(viewer.firstName)}
            </AvatarFallback>
          </Avatar>
          <ChevronDown size={14} aria-hidden className="text-white/80" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm font-medium">{viewer.fullName}</div>
          <div className="truncate text-xs text-stone-500">{viewer.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={dashboardPath({})}>Dashboard</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={userPublicProfilePath({ userId: viewer.id })}>View public profile</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={profileEditPath({})}>Profile</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-stone-700">
          <LogOut size={16} aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-3 py-2 text-base text-stone-700 transition-colors hover:bg-stone-100 hover:text-stone-900",
    isActive && "bg-stone-100 text-stone-900"
  )

const MobileMenu = ({ viewer, canAdmin }: { viewer: AuthedViewer | null; canAdmin: boolean }) => {
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Open menu"
          className="text-white hover:bg-white/10 hover:text-white md:hidden"
        >
          <Menu size={20} aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72">
        <SheetHeader>
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <div className="mt-2 flex flex-col gap-1 px-2">
          {viewer ? (
            <>
              <NavLink to={dashboardPath({})} onClick={close} className={navLinkClass}>
                Dashboard
              </NavLink>
              <NavLink to={registerPuzzlePath({})} onClick={close} className={navLinkClass}>
                Register a puzzle
              </NavLink>
              <NavLink
                to={userPublicProfilePath({ userId: viewer.id })}
                onClick={close}
                className={navLinkClass}
              >
                View public profile
              </NavLink>
              <NavLink to={profileEditPath({})} onClick={close} className={navLinkClass}>
                Profile
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to={rootPath({})} onClick={close} className={navLinkClass}>
                Home
              </NavLink>
              <NavLink to={aboutPath({})} onClick={close} className={navLinkClass}>
                About
              </NavLink>
              <NavLink to={faqPath({})} onClick={close} className={navLinkClass}>
                FAQ
              </NavLink>
              <NavLink to={contactPath({})} onClick={close} className={navLinkClass}>
                Contact
              </NavLink>
              <NavLink to={loginPath({})} onClick={close} className={navLinkClass}>
                Sign in
              </NavLink>
            </>
          )}
          {viewer && canAdmin ? (
            <>
              <div className="mt-4 px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-stone-400">
                Admin
              </div>
              {adminLinks.map((link) => (
                <NavLink key={link.to} to={link.to} onClick={close} className={navLinkClass}>
                  {link.label}
                </NavLink>
              ))}
            </>
          ) : null}
          {viewer ? (
            <button
              type="button"
              onClick={() => {
                close()
                logout()
              }}
              className="mt-4 flex items-center gap-2 rounded-md px-3 py-2 text-left text-base text-stone-700 hover:bg-stone-100"
            >
              <LogOut size={16} aria-hidden />
              Sign out
            </button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export const SiteHeader = () => {
  const { viewer } = useViewerMaybe()
  const { uiAccess } = useUiAccess()
  const canAdmin = !!uiAccess?.canInternalAdmin.value
  const homePath = viewer ? dashboardPath({}) : rootPath({})

  return (
    <header className="sticky top-0 z-40 border-b-[6px] border-mascot-blue bg-mountain text-white">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link
          to={homePath}
          className="flex items-center gap-2 sm:gap-3"
          aria-label="Wandering Puzzles home"
        >
          <img src={mascotUrl} alt="" className="h-12 w-auto sm:h-14" />
          <span className="hidden font-display text-lg font-semibold leading-none sm:inline">
            Wandering Puzzles
          </span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          {viewer && canAdmin ? (
            <div className="hidden md:block">
              <AdminMenu />
            </div>
          ) : null}
          {viewer ? (
            <>
              <Button
                asChild
                size="sm"
                variant="secondary"
                className="hidden bg-white text-mountain hover:bg-white/90 sm:inline-flex"
              >
                <Link to={registerPuzzlePath({})}>Register a puzzle</Link>
              </Button>
              <div className="hidden md:block">
                <UserMenu viewer={viewer} />
              </div>
            </>
          ) : (
            <Button
              asChild
              size="sm"
              variant="secondary"
              className="hidden bg-white text-mountain hover:bg-white/90 sm:inline-flex"
            >
              <Link to={loginPath({})}>Sign in</Link>
            </Button>
          )}
          <div className="md:hidden">
            <MobileMenu viewer={viewer ?? null} canAdmin={canAdmin} />
          </div>
        </div>
        {viewer ? <span className="sr-only">Signed in as {viewer.fullName}</span> : null}
      </div>
    </header>
  )
}
